import type { ReactNode } from "react";
import {
  STAFF_VIEW_READ_ONLY_NOTE_ID,
  useIsStaffView,
  useRegisterSwitchedOffControl,
  useSwitchedOffControlCount,
} from "../../../pages/shell/staffView";
import { cn } from "../primitives/utils";

/**
 * The one sentence staff read where the supplier's page would let the supplier change something and the staff view
 * only looks (docs/features/supplier-portal-view-as/05 §6.5). Dashed box, `role="note"`; provisional wording
 * (docs/ui-wording.md). A page says it once: see `StaffViewReadOnlyNoteOnce`.
 */
export function StaffViewReadOnlyNote({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      id={STAFF_VIEW_READ_ONLY_NOTE_ID}
      role="note"
      className={cn(
        "rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm text-muted-foreground",
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * A page's one read-only note, placed near its first switched-off control: shown while the page has any
 * `StaffViewReadOnly` group, never repeated by them. Nothing outside the staff view.
 */
export function StaffViewReadOnlyNoteOnce({
  sentence,
  className,
}: {
  sentence: string;
  className?: string;
}) {
  const hasSwitchedOffControls = useSwitchedOffControlCount() > 0;
  if (!hasSwitchedOffControls) return null;
  return (
    <StaffViewReadOnlyNote className={className}>
      {sentence}
    </StaffViewReadOnlyNote>
  );
}

/**
 * Draws its controls as the supplier sees them, switched off in the staff view. Outside the staff view it returns its
 * children unchanged, so the supplier build is untouched. In it, an unstyled `<fieldset disabled>` natively disables
 * every descendant button, input and select, and points at the page's one note (`StaffViewReadOnlyNoteOnce`) with
 * `aria-describedby`. Controls are drawn, never hidden (review correction 9); wrap only what changes something:
 * navigation and downloads stay outside.
 */
export function StaffViewReadOnly({
  className,
  children,
}: {
  /** Spacing for the box around the controls. */
  className?: string;
  children: ReactNode;
}) {
  const isStaffView = useIsStaffView();
  useRegisterSwitchedOffControl();
  if (!isStaffView) return <>{children}</>;
  return (
    <fieldset
      disabled
      aria-describedby={STAFF_VIEW_READ_ONLY_NOTE_ID}
      className={cn("m-0 min-w-0 border-0 p-0", className)}
    >
      {children}
    </fieldset>
  );
}
